import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import "./index.css"
import _ from "../../_"
import store from "../../store"
import Spinner from "../../components/info/spinner"
import { useGlobal } from '../../context/global'
import DP from '../../components/custom/dp'

const Login = () => {

  const { api, auth } = useGlobal()

  const InitState = 0
  const UserVerificationPending = 1
  const OTPSendingPending = 2
  const OTPSendingSuccessful = 3
  const OTPVerificationPending = 4
  const SignInPending = 5

  const initUserPrompt = "Enter your registered phone number"
  const expirySeconds = 300
  const resendWaitSeconds = 100

  var [loginState, setLoginState] = useState(InitState)
  var [statusText, setStatusText] = useState("")
  var [resendOTP, setResendOTP] = useState("")
  var [showResendOTP, setShowResendOTP] = useState(false)
  var [buttonText, setButtonText] = useState("Send OTP")
  var [promptText, setPromptText] = useState(initUserPrompt)
  var [inputType, setInputType] = useState("text")
  var [inputMaxLen, setInputMaxLen] = useState(-1)
  var [timerOn, setTimerOn] = useState(false)

  var id = useRef()
  var target = useRef()
  var timerID = useRef()
  var expiry = useRef()
  var users = useRef()
  var delivery = useRef({ mail: false, whatsapp: false })
  var contacts = useRef({
    whatsapp: {},
    email: {},
    both: false,
    neither: false
  })

  const isAddMode = !!new URL(window.location.href).searchParams.get("add")

  const handleSendOTP = () => {
    if (loginState == OTPSendingPending) {
      return
    }
    const phoneInput = id.current.value.trim().toPhoneCase()
    target.current = phoneInput
    setLoginState(UserVerificationPending)
    api.call("/verify-user", { id: phoneInput })
      .then(res => {
        users.current = res.data

        contacts.current.email = {
          has: !!users.current.map(user => user.email).filter(email => !!email).length,
          ids: users.current.map(user => user.email).filter(email => !!email).sonique()
        }

        contacts.current.whatsapp = {
          has: !!users.current.map(user => user.whatsapp).filter(wa => !!wa).length,
          num: users.current.map(user => user.whatsapp)[0]
        }

        contacts.current.both = contacts.current.whatsapp.has && contacts.current.email.has
        contacts.current.neither = !contacts.current.whatsapp.has && !contacts.current.email.has

        setLoginState(OTPSendingPending)
      }).catch(err => {
        setLoginState(InitState)
        if (err && err.response && err.response.status == 404) {
          toast.error("User not found. Check your details or create a new SevaBase account if you are new.")
        } else if (contacts.current.neither) {
          toast.error("Neither WhatsApp number nor email id is found. Unable to send OTP.")
        } else {
          console.log(err)
          toast.error(`User could not be verified!`)
        }
      })
  }

  const handleVerifyOTP = () => {

    if (loginState == UserVerificationPending) {
      return
    }
    setLoginState(OTPVerificationPending)
    api.call("/verify-otp", {
      id: `sevabase-login-${target.current}`,
      otp: id.current.value.trim()
    }).then((res) => {
      setLoginState(SignInPending)
      const { access_token, users: returnedUsers } = res.data
      sessionStorage.setItem("access_token", access_token)
      store.set(
        !isAddMode ? {
          index: 0,
          users: returnedUsers
        } : {
          index: 0,
          users: [...auth.store.users, ...returnedUsers]
        }
      ).then(() => {
        clearInterval(timerID.current)
        window.open(new URL(window.location.href).searchParams.get("redirect") || "/home", "_self")
      }).catch(err => {
        toast.error(err)
      })

    }).catch(err => {
      setLoginState(OTPSendingSuccessful)
      if (err.response.status == 403) {
        id.current.value = ""
        toast.error(`Incorrect OTP. Please enter again.`)
      } else if (err.response.status == 408) {
        id.current.value = ""
        toast.error(`Request timed-out. Please try again`)
      } else {
        toast.error(`Could not verify OTP: ${err.response.data}. Please try again!`)
      }
    })
  }

  useEffect(() => {
    if (loginState == InitState) {
      setStatusText("")
      setButtonText("Send OTP")
      setPromptText(initUserPrompt)
      setInputType("tel")
      setInputMaxLen(-1)
    } else if (loginState == UserVerificationPending) {
      setStatusText("Verifying user..")
      setButtonText("Verifying user..")
    } else if (loginState == OTPSendingPending) {
      setStatusText("Sending OTP. This may take a couple of minutes...")
      setButtonText("Sending OTP...")
    } else if (loginState == OTPSendingSuccessful) {

      setButtonText("Verify OTP")
      // Build prompt from actual delivery result, not just what channels the user has
      const deliveredVia = [
        delivery.current.whatsapp ? `WhatsApp (${contacts.current.whatsapp.num || target.current})` : '',
        delivery.current.mail    ? `E-mail (${contacts.current.email.ids?.and?.() || ''})` : ''
      ].filter(p => !!p)
      setPromptText(`Enter the 6-digit OTP sent to your ${deliveredVia.length ? deliveredVia.join(' or ') : 'registered contact'}`)
      setTimerOn(true)
      setInputType("number")
      setInputMaxLen(6)
      id.current.focus()
      if (!expiry.current) {
        expiry.current = moment().add(expirySeconds, 'seconds')
      }
    } else if (loginState == OTPVerificationPending) {
      setButtonText("Verifying OTP...")
    } else if (loginState == SignInPending) {
      setStatusText("Signing you in..")
      setTimerOn(false)
    } else {
      setStatusText("")
    }
  }, [loginState])

  useEffect(() => {
    if (loginState == OTPSendingPending) {
      clearInterval(timerID.current)
      const value = target.current

      api.call("/send-otp", {
        id: `sevabase-login-${value}`,
        target: value,
        emails: contacts.current.email.ids
      }).then((res) => {
        // Store actual delivery channels from OTP service response
        delivery.current = res.data?.delivery || { mail: false, whatsapp: false }
        setLoginState(OTPSendingSuccessful)
        setShowResendOTP(false)
        id.current.value = ""
      }).catch(err => {
        console.log(err)
        setLoginState(InitState)
        const errMsg = err.response?.data || 'Unknown error'
        toast.error(`Could not send OTP: ${typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg}. Please try again!`)
      })
    }
  }, [loginState])

  useEffect(() => {
    if (timerOn) {
      setStatusText(`OTP will expire in 05:00`)
      timerID.current = setInterval(() => {
        const secondsRemaining = moment.utc(expiry.current.diff(moment())) / 1000
        if (secondsRemaining <= 1) {
          setTimerOn(false)
          setLoginState(InitState)
          toast.error("OTP expired! Please try again.")
          return
        }
        if (Math.floor(secondsRemaining) == expirySeconds - resendWaitSeconds) {
          setShowResendOTP(true)
        }
        setStatusText(`OTP will expire in ${moment.utc(expiry.current.diff(moment())).format('mm:ss')}`)
        if (!showResendOTP) {
          setResendOTP(`You can request to resend OTP after ${moment.utc(moment(expiry.current).subtract(expirySeconds - resendWaitSeconds, 'seconds').diff(moment())).format('mm:ss')}`)
        }
      }, 1000)
    } else {
      clearInterval(timerID.current)
    }
  }, [timerOn, showResendOTP])

  const handleSubmit = (e) => {
    if (e.code == "Enter") {
      if (loginState == OTPSendingSuccessful) {
        handleVerifyOTP()
      } else {
        handleSendOTP()
      }
    }
  }

  const handleResendOTP = () => {
    expiry.current = moment().add(expirySeconds, 'seconds')
    setLoginState(OTPSendingPending)
  }

  return (
    <div className="login-container">

      <div className='login-logo-cont'>
        <DP url="/favicon.png" size={[50, 15]} />
      </div>

      <div className='login-status-cont'>
        <div className='login-status'>{statusText}</div>
        {loginState == SignInPending ? <Spinner size={5} /> : null}
      </div>

      {loginState < SignInPending ?
        <div className='login-form'>
          <label className='login-label'>{promptText}</label>
          <input
            className="login-input"
            ref={id}
            type={inputType}
            maxLength={inputMaxLen}
            onKeyDown={handleSubmit}
          />

          <button
            className="login-send-button"
            onClick={loginState == OTPSendingSuccessful ? handleVerifyOTP : handleSendOTP}
            disabled={loginState == OTPSendingPending || loginState == OTPVerificationPending}
          >
            {buttonText}
          </button>

          {loginState == OTPSendingSuccessful &&
            <div className='login-buttons'>
              {!showResendOTP && <div className='login-resend-info'>{resendOTP}</div>}
              {showResendOTP && <button className='login-send-button login-resend-button' onClick={handleResendOTP}>Resend OTP</button>}
            </div>
          }
        </div> : null
      }

      {loginState != SignInPending &&
        <div className='login-register'>
          <div className='login-reg-prompt'>Don't have an account yet?</div>
          <a className='login-reg-link' href='/signup' target='_self'><button className='login-reg-button'>Create Account</button></a>
        </div>}

    </div>
  )
}

export default Login